<template>
  <div class="home">
    <!-- Hero Slider -->
    <div class="hero-slider" v-if="slides.length > 0">
      <div class="slider-container">
        <div
          ref="slidesTrack"
          class="slides"
          :style="slideTrackStyle"
          @transitionend="onTrackTransitionEnd"
          @touchstart="onPointerStart"
          @touchmove="onPointerMove"
          @touchend="onPointerEnd"
          @mousedown="onPointerStart"
          @mousemove="onPointerMove"
          @mouseup="onPointerEnd"
          @mouseleave="onPointerCancel"
        >
          <div v-for="(slide, index) in slides" :key="index" class="slide">
            <!-- src is withheld (null -> attribute omitted, no request) until the
                 slide is released by revealSlidesAround/revealAllSlides. Binding
                 every slide's image up front made all of them race the hero. -->
            <img
              :src="slideSrcs[index] || null"
              :alt="slide.title"
              :fetchpriority="index === currentSlide ? 'high' : null"
              decoding="async"
            >
            <div class="slide-content">
              <div class="slide-text">
                <h2 class="slide-subtitle">{{ slide.description }}</h2>
                <h1 class="slide-title">{{ slide.title }}</h1>
                <button class="cta-button" @click="navigateToLink(slide.link)">
                  SEE MORE
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Arrows -->
        <button class="nav-button prev" @click="prevSlide" v-if="slides.length > 1">
          <span class="arrow">&#10094;</span>
        </button>
        <button class="nav-button next" @click="nextSlide" v-if="slides.length > 1">
          <span class="arrow">&#10095;</span>
        </button>

        <!-- Dot Indicators -->
        <div class="dot-indicators" v-if="realSlideCount > 1">
          <button
            v-for="index in realSlideCount"
            :key="index"
            :class="['dot', { active: getRealIndex() === (index - 1) }]"
            @click="goToSlide(index - 1)"
          ></button>
        </div>
      </div>
    </div>

    <!-- New Selection -->
    <section class="new-selection-section" v-if="showNewSelection">
      <div class="new-selection-header">
        <h2 class="new-selection-title">New Selection</h2>
        <router-link :to="newSelectionSeeAllTo" class="new-selection-see-all">
          See all <span aria-hidden="true">&rarr;</span>
        </router-link>
      </div>

      <div class="new-selection-grid">
        <product-card
          v-for="(item, index) in newSelectionItems"
          :key="`new-selection-${item.id || index}`"
          :image="item.image"
          :title="item.title"
          :price="item.price"
          :original-price="item.originalPrice"
          :seller-name="item.sellerName"
          :seller-image="item.sellerImage"
          :favourited="isFavourited(item)"
          :popping="poppingFavouriteUrl === getFavouriteUrl(item)"
          @open="openNewSelectionItem(item)"
          @toggle-favourite="toggleFavourite(item)"
        />
      </div>

      <router-link :to="newSelectionSeeAllTo" class="new-selection-see-all-bottom">
        See all <span aria-hidden="true">&rarr;</span>
      </router-link>
    </section>

    <div v-if="loading" class="loading">Loading...</div>
    <div v-if="error" class="error">{{ error }}</div>
  </div>
</template>

<script>
import axios from 'axios';
import config from '../config';
import productNavigation from '../services/productNavigation';
import favouritesMixin from '../mixins/favouritesMixin';
import ProductCard from '../components/ProductCard.vue';

export default {
  name: 'Home',
  components: { ProductCard },
  mixins: [favouritesMixin],
  data() {
    return {
      currentSlide: 0,
      slides: [],
      // Parallel to `slides`: holds the image URL once that slide is allowed to
      // download, and a falsy value while it is still being held back.
      slideSrcs: [],
      realSlides: [],
      newSelection: [],
      loading: false,
      error: null,
      autoplayInterval: null,
      transitionEnabled: true,
      realSlideCount: 0,
      isResetting: false,
      isAnimating: false,
      trackTransitionMs: 550,
      pointer: {
        active: false,
        startX: 0,
        lastX: 0,
        isTouch: false,
        moved: false
      },
      decodedImages: new Set()
    }
  },
  computed: {
    slideTrackStyle() {
      const x = this.currentSlide * 100;
      return {
        transform: `translate3d(-${x}%, 0, 0)`,
        transition: this.transitionEnabled ? `transform ${this.trackTransitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)` : 'none'
      };
    },
    newSelectionItems() {
      return Array.isArray(this.newSelection) ? this.newSelection : [];
    },
    showNewSelection() {
      return this.newSelectionItems.length > 0;
    },
    newSelectionSeeAllTo() {
      return { name: 'NewSelection' };
    }
  },
  methods: {
    getRandomInitialTrackIndex(realSlideCount) {
      if (!Number.isFinite(realSlideCount) || realSlideCount <= 1) return 1;
      return Math.floor(Math.random() * realSlideCount) + 1;
    },
    async getAuthToken() {
      try {
        // Try to get token from the secure endpoint
        const response = await fetch('/api/auth/token', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include' // Use cookies/session for authentication
        });
        
        const data = await response.json();
        
        if (!response.ok) {
          console.error('Failed to get token from secure endpoint:', data);
          
          // No fallback needed - session-based auth
          
          return null;
        }
        
        console.log('Token received successfully from secure endpoint');
        return data.token;
      } catch (error) {
        console.error('Error getting auth token:', error);
        
        // No fallback needed - session-based auth
        
        return null;
      }
    },

    async fetchWithAuth(url) {
      let token = localStorage.getItem('authToken');
      
      const fetchWithHeaders = async (token) => {
        return await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          credentials: 'same-origin'
        });
      };

      try {
        let response = await fetchWithHeaders(token);

        if (response.status === 401) {
          // Token expired, get new token and retry
          token = await this.getAuthToken();
          response = await fetchWithHeaders(token);
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        return response;
      } catch (error) {
        console.error('Error fetching with auth:', error);
        throw error;
      }
    },

    async fetchNewSelection() {
      try {
        const response = await fetch(`${config.apiBaseUrl}/api/homepage/new-selection?limit=40`);
        if (!response.ok) throw new Error(`Request failed: ${response.status}`);
        const data = await response.json();
        const items = Array.isArray(data.products) ? data.products : [];

        this.newSelection = items.map(item => ({
          id: item.id ?? item.product_id ?? null,
          categoryId: item.categoryId ?? item.category_id ?? null,
          // Collection products expose `imageUrl`/`description`, not `image`/`title`.
          title: item.title || item.description || item.name || '',
          price: item.price || '',
          originalPrice: item.originalPrice || '',
          link: item.link || item.url || '',
          productLink: item.productLink || null,
          image: item.imageUrl || item.image || '',
          sellerName: item.sellerName || '',
          sellerImage: item.sellerImageUrl || ''
        }));
      } catch (error) {
        // New Selection is supplementary; a failure here must not blank the homepage.
        console.error('Error fetching new selection:', error);
        this.newSelection = [];
      }
    },

    async fetchData() {
      try {
        this.loading = true;
        // First fetch homepage data for slides and categories
        const response = await fetch(`${config.apiBaseUrl}/api/homepage/public`);
        const data = await response.json();
        
        // Prepare slides with full image URLs
        const preparedSlides = (data.slides || []).map(slide => ({
          ...slide,
          image: slide.image && !slide.image.startsWith('http') ? `${config.apiBaseUrl}${slide.image}` : slide.image
        }));

        const initialTrackIndex = this.getRandomInitialTrackIndex(preparedSlides.length);
        const initialRealSlide = preparedSlides.length > 1
          ? preparedSlides[initialTrackIndex - 1]
          : preparedSlides[0];

        this.realSlides = preparedSlides;
        this.realSlideCount = this.realSlides.length;
        // Build an infinite track: [lastClone, ...realSlides, firstClone]
        if (this.realSlideCount > 1) {
          const firstClone = { ...this.realSlides[0] };
          const lastClone = { ...this.realSlides[this.realSlideCount - 1] };
          this.slides = [lastClone, ...this.realSlides, firstClone];
          this.currentSlide = initialTrackIndex;
        } else {
          this.slides = [...this.realSlides];
          this.currentSlide = 0;
        }

        // Only the slide that is actually on screen gets a src for now. The rest
        // are released once the hero has arrived (see whenSlideLoaded below).
        this.slideSrcs = this.slides.map((slide, index) => (
          index === this.currentSlide ? (slide.image || '') : ''
        ));



        // Render the page now — the hero <img> downloads on its own. Blocking here on
        // the first slide used to hold the whole view behind the loading state for as
        // long as that JPEG took to arrive, which on a cold visit was seconds.
        // Autoplay and the neighbour preloads wait for the hero so they don't compete
        // with it for bandwidth, and so nobody gets paged through blank slides.
        if (this.slides.length > 0) {
          this.whenSlideLoaded(initialRealSlide).then(() => {
            // The visitor may have navigated away while the hero was still downloading.
            if (this._destroyed) return;
            this.startAutoplay();
            this.revealAllSlides();
            this.preloadAround(initialTrackIndex - 1);
            this.preloadRemainingSlides(initialRealSlide);
          });
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.error = 'Failed to load data';
      } finally {
        this.loading = false;
      }
    },

    getRealIndex() {
      if (this.realSlideCount <= 1) return 0;
      // currentSlide: 1..realSlideCount correspond to real slides
      let idx = this.currentSlide - 1;
      if (idx < 0) idx = this.realSlideCount - 1;
      if (idx >= this.realSlideCount) idx = 0;
      return idx;
    },

    prevSlide() {
      if (this.isResetting || this.isAnimating) return;
      if (this.realSlideCount <= 1) return;

      this.revealSlidesAround(this.currentSlide - 1);
      this.preloadAround(this.getRealIndex() - 1);

      if (this.currentSlide > 0) {
        this.isAnimating = true;
        this.currentSlide--;
      }
    },
    // Releases the <img src> for a track index and its immediate neighbours, so a
    // visitor who clicks through before the hero has settled never sees a blank
    // slide. Already-released slides are left alone.
    revealSlidesAround(trackIndex) {
      if (!this.slides.length) return;

      const last = this.slides.length - 1;
      const from = Math.max(0, trackIndex - 1);
      const to = Math.min(last, trackIndex + 1);

      for (let i = from; i <= to; i++) {
        const image = this.slides[i] && this.slides[i].image;
        if (image && !this.slideSrcs[i]) this.$set(this.slideSrcs, i, image);
      }
    },
    revealAllSlides() {
      this.slides.forEach((slide, i) => {
        const image = slide && slide.image;
        if (image && !this.slideSrcs[i]) this.$set(this.slideSrcs, i, image);
      });
    },
    preloadAround(realIndex) {
      if (!this.realSlides || this.realSlides.length === 0) return;

      const count = this.realSlides.length;

      const preload = (i) => {
        const idx = (i + count) % count;
        const slide = this.realSlides[idx];
        if (!slide || !slide.image) return;

        if (this.decodedImages.has(slide.image)) return;
        this.decodedImages.add(slide.image);

        const img = new Image();
        img.src = slide.image;
        img.decode?.().catch(() => {});
      };

      preload(realIndex + 1);
      preload(realIndex + 2);
    },
    // Resolves once the given slide's image has finished loading. Resolves immediately
    // when there is nothing to wait for, and on error too — a broken image must not
    // leave autoplay permanently stopped.
    whenSlideLoaded(slide) {
      if (!slide || !slide.image) return Promise.resolve();

      this.decodedImages.add(slide.image);

      return new Promise((resolve) => {
        const img = new Image();
        img.onload = resolve;
        img.onerror = resolve;
        img.src = slide.image;
      });
    },
    preloadRemainingSlides(exceptSlide) {
      const exceptImage = exceptSlide && exceptSlide.image;

      this.realSlides.forEach((slide) => {
        if (!slide || !slide.image || slide.image === exceptImage) return;
        if (this.decodedImages.has(slide.image)) return;
        this.decodedImages.add(slide.image);

        const img = new Image();
        img.src = slide.image;
        // Force decode to prevent jank during transition
        img.decode?.().catch(() => {});
      });
    },
    nextSlide() {
      if (this.isResetting || this.isAnimating) return;
      if (this.realSlideCount <= 1) return;

      this.revealSlidesAround(this.currentSlide + 1);
      this.preloadAround(this.getRealIndex());

      const lastIndex = this.slides.length - 1;
      if (this.currentSlide < lastIndex) {
        this.isAnimating = true;
        this.currentSlide++;
      }
    },
    goToSlide(index) {
      if (this.isResetting || this.isAnimating) return;
      if (this.realSlideCount <= 1) return;
      // Real index -> track index (+1 because of leading clone)

      this.revealSlidesAround(index + 1);
      this.preloadAround(index);

      const target = index + 1;
      if (target >= 0 && target <= this.slides.length - 1) {
        this.isAnimating = true;
        this.currentSlide = target;
      }
    },

    onTrackTransitionEnd() {
      if (this.realSlideCount <= 1) return;

      // When we hit clones, jump without transition to the corresponding real slide.
      // Track indices: 0 = lastClone, 1..N = real slides, N+1 = firstClone
      const lastRealTrackIndex = this.realSlideCount;
      const firstCloneIndex = this.slides.length - 1;
      const lastCloneIndex = 0;

      if (this.currentSlide === firstCloneIndex) {
        // Moved past last real -> snap to first real
        this.isResetting = true;
        this.transitionEnabled = false;
        this.revealSlidesAround(1);
        this.currentSlide = 1;
        this.$nextTick(() => {
          // Force style flush so transform applies with transition disabled
          try {
            // eslint-disable-next-line no-unused-expressions
            this.$refs.slidesTrack && this.$refs.slidesTrack.offsetHeight;
          } catch (e) {}

          // Keep transition disabled for an extra frame to avoid accidental tween
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.transitionEnabled = true;
              this.isResetting = false;
              this.isAnimating = false;
            });
          });
        });
      } else if (this.currentSlide === lastCloneIndex) {
        // Moved before first real -> snap to last real
        this.isResetting = true;
        this.transitionEnabled = false;
        this.revealSlidesAround(lastRealTrackIndex);
        this.currentSlide = lastRealTrackIndex;
        this.$nextTick(() => {
          // Force style flush so transform applies with transition disabled
          try {
            // eslint-disable-next-line no-unused-expressions
            this.$refs.slidesTrack && this.$refs.slidesTrack.offsetHeight;
          } catch (e) {}

          // Keep transition disabled for an extra frame to avoid accidental tween
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.transitionEnabled = true;
              this.isResetting = false;
              this.isAnimating = false;
            });
          });
        });
      } else {
        this.isAnimating = false;
      }
    },

    // Pointer / swipe support
    onPointerStart(e) {
      if (this.realSlideCount <= 1) return;
      // only left mouse button
      if (e.type === 'mousedown' && e.button !== 0) return;
      this.pointer.active = true;
      this.pointer.moved = false;
      this.pointer.isTouch = e.type.startsWith('touch');
      const clientX = this.pointer.isTouch ? e.touches[0].clientX : e.clientX;
      this.pointer.startX = clientX;
      this.pointer.lastX = clientX;
    },
    onPointerMove(e) {
      if (!this.pointer.active) return;
      const clientX = this.pointer.isTouch ? (e.touches?.[0]?.clientX ?? this.pointer.lastX) : e.clientX;
      const dx = clientX - this.pointer.startX;
      this.pointer.lastX = clientX;
      if (Math.abs(dx) > 6) this.pointer.moved = true;
    },
    onPointerEnd() {
      if (!this.pointer.active) return;
      this.pointer.active = false;
      if (!this.pointer.moved) return;

      const dx = this.pointer.lastX - this.pointer.startX;
      const threshold = 40;
      if (dx > threshold) {
        // swipe right -> previous
        this.prevSlide();
      } else if (dx < -threshold) {
        // swipe left -> next
        this.nextSlide();
      }
    },
    onPointerCancel() {
      this.pointer.active = false;
    },

    startAutoplay() {
      this.stopAutoplay(); // Clear any existing interval
      this.autoplayInterval = setInterval(() => {
        this.nextSlide();
      }, 5000);
    },
    stopAutoplay() {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval);
      }
    },
    navigateToLink(link) {
      // Slide CTAs are admin-configured free text; openLink keeps a marketplace
      // URL that slipped into the CMS on our own product page.
      productNavigation.openLink(this.$router, link);
    },
    openNewSelectionItem(item) {
      // Always stays inside Curator Eden — never follow the Goofish source URL.
      productNavigation.openProduct(this.$router, item);
    }
  },
  mounted() {
    this.fetchData();
    this.fetchNewSelection();
  },
  beforeDestroy() {
    this._destroyed = true;
    this.stopAutoplay();
  }
}
</script>

<style scoped>
.home {
  width: 100%;
  margin: 0;
  padding: 0;
}

.hero-slider {
  width: 100vw;
  position: relative;
  background: #000;
  margin-left: calc(50% - 50vw);
  margin-right: calc(50% - 50vw);
}

.slider-container {
  width: 100%;
  height: 65vh;
  position: relative;
  overflow: hidden;
}

.slides {
  display: flex;
  transition: transform 0.5s ease-in-out;
  height: 100%;
  will-change: transform;
  backface-visibility: hidden;
}

.slide {
  min-width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  filter: brightness(0.85);
  transform: translateZ(0);
}

.slide-content {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  padding: 20px;
  background: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.6));
}

.slide-text {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.slide-subtitle {
  font-size: 1.2rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  margin-bottom: 1rem;
  font-weight: 500;
}

.slide-title {
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  line-height: 1.2;
}

.cta-button {
  background: white;
  color: black;
  border: none;
  padding: 15px 30px;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cta-button:hover {
  background: rgba(255, 255, 255, 0.9);
  transform: translateY(-2px);
}

.nav-button {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.2);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
}

.nav-button:hover {
  background: rgba(0, 0, 0, 0.6);
}

.prev {
  left: 20px;
}

.next {
  right: 20px;
}

.arrow {
  font-size: 24px;
}

.dot-indicators {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 10;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: none;
  cursor: pointer;
  padding: 0;
  transition: all 0.3s ease;
}

.dot.active {
  background: white;
  transform: scale(1.2);
}

/* New Selection Styles */
.new-selection-section {
  max-width: 1200px;
  margin: 60px auto;
  padding: 0 20px;
}

.new-selection-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
}

.new-selection-title {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  letter-spacing: -0.02em;
}

.new-selection-see-all {
  flex-shrink: 0;
  color: #1b4b8f;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
}

.new-selection-see-all:hover {
  text-decoration: underline;
}

.new-selection-see-all-bottom {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin: 32px auto 0;
  padding: 12px 32px;
  width: fit-content;
  border: 1px solid #1a1a1a;
  border-radius: 2px;
  color: #1a1a1a;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.new-selection-see-all-bottom:hover {
  background: #1a1a1a;
  color: #fff;
}

.new-selection-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}







/* Responsive Styles */
@media (max-width: 1200px) {
  .slider-container {
    height: 60vh;
  }
}

@media (max-width: 768px) {
  .new-selection-section {
    margin: 24px auto 0;
    padding: 8px 15px 0;
  }

  .new-selection-header {
    margin-bottom: 18px;
  }

  .new-selection-title {
    font-size: 1.5rem;
  }

  .new-selection-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px 15px;
  }

  .slider-container {
    height: 36vh;
  }

  .slide-title {
    font-size: 2.5rem;
  }

  .slide-subtitle {
    font-size: 1rem;
  }

  .cta-button {
    padding: 12px 25px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .new-selection-section {
    margin: 18px auto 0;
    padding: 6px 10px 0;
  }

  .new-selection-title {
    font-size: 1.25rem;
  }

  .new-selection-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px 10px;
  }

  .slider-container {
    height: 30vh;
  }

  .slide-title {
    font-size: 1.6rem;
  }

  .slide-subtitle {
    font-size: 0.8rem;
    letter-spacing: 2px;
    margin-bottom: 0.6rem;
  }

  .cta-button {
    padding: 9px 16px;
    font-size: 0.78rem;
    letter-spacing: 1.4px;
  }
}

.loading {
  text-align: center;
  padding: 40px;
  font-size: 1.2rem;
  color: #666;
}

.error {
  text-align: center;
  padding: 40px;
  color: #dc3545;
  font-size: 1.1rem;
}
</style> 
